import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/userSlice";
import "./css/AdminLayout.css";
import MenuIcon from "@mui/icons-material/Menu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Outlet,
  Link,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  Box,
  CssBaseline,
  Drawer,
  Toolbar,
  AppBar,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  ListItemIcon,
  Button,
  Tooltip,
} from "@mui/material";

const drawerWidth = 240; // ✅ sabit drawer genişliği

const AdminLayout = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user.currentUser);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loadingLogout, setLoadingLogout] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const handleLogout = () => {
    setLoadingLogout(true);
    setTimeout(() => {
      dispatch(logout());
      setSnackbarOpen(true);
      setLoadingLogout(false);
      navigate("/");
      window.scrollTo(0, 0);
    }, 1000);
  };

  const navItems = [
    { text: "Dashboard", path: "/admin", icon: "bi bi-speedometer2" },
    { text: "Müşteriler", path: "/admin/customers", icon: "bi bi-people" },
    { text: "Seanslar", path: "/admin/sessions", icon: "bi bi-calendar-event" },
    { text: "Paketler", path: "/admin/packages", icon: "bi bi-box-seam" },
    { text: "Giderler", path: "/admin/expenses", icon: "bi bi-cash-stack" },
    {
      text: "Randevular",
      path: "/admin/appointments",
      icon: "bi bi-calendar-check",
    },
    { text: "Mesajlar", path: "/admin/messages", icon: "bi bi-chat-left-text" },
    { text: "Blog", path: "/admin/posts", icon: "bi bi-file-text" },
    { text: "Yorumlar", path: "/admin/testimonials", icon: "bi bi-chat-dots" },
    { text: "Kategoriler", path: "/admin/categories", icon: "bi bi-folder" },
    { text: "Etiketler", path: "/admin/tags", icon: "bi bi-tag" },
    { text: "Kullanıcılar", path: "/admin/users", icon: "bi bi-people" },
  ];

  if (!user || (user.role !== "admin" && user.role !== "editor")) {
    return (
      <div className="unauthorized">
        <div className="alert">Bu sayfaya erişim yetkiniz yok.</div>
      </div>
    );
  }

  const drawerContent = (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
        Admin Panel
      </Typography>
      <List>
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const isDisabled =
            user?.role === "editor" && item.path !== "/admin/posts";

          return (
            <ListItem disablePadding key={item.text}>
              <ListItemButton
                component={isDisabled ? "div" : Link}
                to={isDisabled ? undefined : item.path}
                onClick={() => !isDisabled && setMobileOpen(false)}
                disabled={isDisabled}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: isActive ? "#f0f0f0" : "transparent",
                  opacity: isDisabled ? 0.5 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  pointerEvents: isDisabled ? "none" : "auto",
                  "&:hover": {
                    bgcolor: isDisabled ? "transparent" : "#e0e0e0",
                  },
                }}
              >
                
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? "bold" : 500,
                    color: isDisabled
                      ? "gray"
                      : isActive
                      ? "primary"
                      : "inherit",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: "white",
          color: "black",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          borderBottom: "1px solid #ddd",
          zIndex: 1201,
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: 600, flexGrow: 1 }}
          >
            Dilan Aslan
          </Typography>

          <Tooltip title="Blog Ana Sayfasına Git">
            <Button
              color="inherit"
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: "none", ml: 1 }}
            >
              Sayfaya dön
            </Button>
          </Tooltip>
          <Tooltip title="Blog Ana Sayfasına Git">
            <Button
              color="inherit"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: "none", ml: 1, color: "red" }}
              onClick={handleLogout}
            >
              Çıkış Yap
            </Button>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Drawer – Mobile */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "white",
              borderRight: "1px solid #ddd",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Drawer – Desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "white",
              borderRight: "1px solid #ddd",
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: "#fafafa",
          px: { xs: 2, md: 3 },
          pb: 5,
          pt: { xs: 4, md: 6 },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
